import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Bookmark, ChevronLeft, ChevronRight, Link2, Sparkles } from "lucide-react";
import { api } from "../lib/api";
import { formatUsd } from "../lib/format";
import { useAuth } from "../store/auth";
import { PriceChart, type ChartTick } from "./PriceChart";
import { type EventGroupCard, type Market } from "./MarketCard";

const RANGES: { id: string; api: string }[] = [
  { id: "1H", api: "1H" },
  { id: "6H", api: "6H" },
  { id: "1D", api: "1D" },
  { id: "1W", api: "1W" },
  { id: "1M", api: "1M" },
  { id: "ALL", api: "ALL" },
];

export function pickFeatured(list: Market[], n = 6): Market[] {
  const seen = new Set<string>();
  const out: Market[] = [];
  for (const m of list) {
    const key = m.event_slug || m.id;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(m);
    if (out.length >= n) break;
  }
  return out.length ? out : list.slice(0, n);
}

export function FeaturedHero({
  markets,
  groups,
}: {
  markets: Market[];
  groups: EventGroupCard[];
}) {
  const [i, setI] = useState(0);
  const [range, setRange] = useState("1D");
  const [copied, setCopied] = useState(false);
  const { user } = useAuth();
  const nav = useNavigate();
  const market = markets[Math.min(i, Math.max(0, markets.length - 1))];
  const rangeApi = RANGES.find((r) => r.id === range)?.api ?? "1D";
  const chart = useQuery({
    queryKey: ["chart", market?.id, rangeApi],
    queryFn: () => api<{ ticks: ChartTick[] }>(`/markets/${market!.id}/chart?range=${rangeApi}`),
    enabled: !!market,
  });
  const watch = useMutation({
    mutationFn: () => api(`/markets/${market!.id}/watch`, { method: "POST" }),
  });

  if (!market) {
    return (
      <section className="hero">
        <div className="hero-inner">
          <div>
            <span className="kicker">Prediction exchange</span>
            <h1>Trade on what happens next.</h1>
            <p className="lede">Live markets will appear here as soon as a book is open.</p>
            <div className="hero-cta">
              <Link to="/markets" className="btn btn-primary btn-lg">Browse markets</Link>
            </div>
          </div>
        </div>
      </section>
    );
  }

  const n = markets.length;
  const prev = () => setI((v) => (v - 1 + n) % n);
  const next = () => setI((v) => (v + 1) % n);
  const yes = market.yes_price_cents;
  const no = market.no_price_cents;
  const hot = [...groups].sort((a, b) => Number(b.volumeMicro) - Number(a.volumeMicro)).slice(0, 6);
  const shareUrl = `${typeof window !== "undefined" ? window.location.origin : ""}/markets/${market.slug}`;
  const question = market.question;

  async function share() {
    try {
      if (navigator.share) {
        await navigator.share({ title: question, url: shareUrl });
        return;
      }
    } catch {
      /* fall through to copy */
    }
    await navigator.clipboard.writeText(shareUrl);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1600);
  }

  return (
    <section className="hero hero-stage">
      <div className="hero-stage-inner">
        <article className="card feature-stage">
          <header className="feature-head">
            <Link to={`/markets/${market.slug}`} className="feature-ident">
              {market.image_url ? (
                <img src={market.image_url} alt="" className="feature-thumb" />
              ) : (
                <span className="feature-thumb feature-thumb-fallback" aria-hidden>
                  {market.category_icon || "●"}
                </span>
              )}
              <div>
                <span className="badge badge-live">{market.category_name}</span>
                <h1 className="feature-title">{market.question}</h1>
              </div>
            </Link>
            <div className="feature-tools">
              <button type="button" className="icon-btn" aria-label="Copy market link" onClick={() => void share()}>
                <Link2 size={18} />
              </button>
              <button
                type="button"
                className="icon-btn"
                aria-label="Watch this market"
                onClick={() => {
                  if (!user) {
                    nav("/login");
                    return;
                  }
                  watch.mutate();
                }}
              >
                <Bookmark size={18} fill={watch.isSuccess ? "currentColor" : "none"} />
              </button>
              {n > 1 && (
                <div className="feature-pager">
                  <button type="button" className="icon-btn" aria-label="Previous featured market" onClick={prev}>
                    <ChevronLeft size={20} />
                  </button>
                  <span className="feature-dots" aria-hidden>
                    {markets.map((m, idx) => (
                      <i key={m.id} className={idx === i ? "on" : ""} />
                    ))}
                  </span>
                  <button type="button" className="icon-btn" aria-label="Next featured market" onClick={next}>
                    <ChevronRight size={20} />
                  </button>
                </div>
              )}
            </div>
          </header>

          <div className="feature-body">
            <div className="feature-trade">
              <div className="feature-chance">
                <strong>{yes}%</strong>
                <span>Yes</span>
              </div>
              <div className="yesno feature-yesno">
                <Link className="pill-yes" to={`/markets/${market.slug}?buy=YES`}>
                  <span>Yes</span>
                  <span>{yes}¢ / share</span>
                </Link>
                <Link className="pill-no" to={`/markets/${market.slug}?buy=NO`}>
                  <span>No</span>
                  <span>{no}¢ / share</span>
                </Link>
              </div>
              <div className="feature-meta">
                <span>{formatUsd(market.volume_total_micro, true)} vol.</span>
                <span>Closes {market.timeRemaining}</span>
              </div>
              {copied && <p className="feature-copied">Link copied.</p>}
            </div>
            <div className="feature-chart">
              <div className="feature-chart-h">
                <strong>Probability history</strong>
                <span className="feature-legend">
                  <i className="yes" /> Yes {yes}%
                  <i className="no" /> No {no}%
                </span>
              </div>
              <div className="seg feature-ranges" role="tablist" aria-label="Chart range">
                {RANGES.map((r) => (
                  <button key={r.id} type="button" className={range === r.id ? "on" : ""} onClick={() => setRange(r.id)}>
                    {r.id}
                  </button>
                ))}
              </div>
              <PriceChart
                ticks={chart.data?.ticks ?? []}
                compact
                live
                fallbackCents={yes}
              />
            </div>
          </div>
        </article>

        <aside className="feature-side">
          <div className="card feature-promo">
            <Sparkles size={22} />
            <div>
              <strong>New here?</strong>
              <p>Buy Yes or No from other traders. Winning contracts settle at $1.00.</p>
            </div>
            <Link to={user ? "/markets" : "/register"} className="btn btn-navy btn-sm">
              {user ? "Browse markets" : "Create account"}
            </Link>
          </div>
          {hot.length > 0 && (
            <div className="card hot-topics">
              <div className="hot-head">
                <strong>Hot topics</strong>
                <span>Lifetime volume</span>
              </div>
              <ol>
                {hot.map((g, idx) => (
                  <li key={g.slug}>
                    <Link to={`/topics/${g.slug}`}>
                      <span className="hot-rank">{idx + 1}</span>
                      <span className="hot-title">{g.title}</span>
                      <span className="hot-vol">{formatUsd(g.volumeMicro, true)}</span>
                    </Link>
                  </li>
                ))}
              </ol>
              <Link to="/markets" className="btn btn-ghost btn-block">Explore all</Link>
            </div>
          )}
        </aside>
      </div>
    </section>
  );
}
